
import {
  computed, defineComponent, PropType, ref, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from '@/store';
import moment from 'moment';
import Dialog from 'primevue/dialog';
import ReviewAndPayCard, { ReviewAndPayLine } from '@/components/billing/shared/ReviewAndPayCard.vue';
import {
  ActionResult,
  ALLOWED_METHODS_ONESHOT,
  confirmPlan,
  repayInvoice,
} from '@/composables/billing/billingApi';
import { useStructuralReload } from '@/composables/billing/useStructuralReload';
import { InvoiceListItem, PaymentMethodItem } from '@/types/billing-types';

type PaymentMode = 'existing' | 'new';

export default defineComponent({
  name: 'RepayInvoiceDialog',

  components: { Dialog, ReviewAndPayCard },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },

    invoice: {
      type: Object as PropType<InvoiceListItem | null>,
      default: null,
    },
  },

  emits: ['update:visible', 'paid'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const store = useStore();
    const handleReload = useStructuralReload();

    const submitting = ref(false);
    const paymentError = ref<string | null>(null);
    const paymentMode = ref<PaymentMode>('existing');
    const paymentMethodId = ref<string | null>(null);

    const overview = computed(() => store.state.billing.overview);
    const allCards = computed<PaymentMethodItem[]>(
      () => overview.value?.paymentMethods?.items ?? [],
    );
    const defaultCard = computed(() => (
      allCards.value.find((p: any) => p.isDefault) ?? null
    ));
    const hasDefaultCard = computed(() => defaultCard.value !== null);

    const formattedDate = computed(() => (
      props.invoice ? moment(props.invoice.date).format('D MMMM YYYY') : ''
    ));

    // Single « Montant HT » line — visually mirrors the WalletTopUpReview recap
    // where a short breakdown precedes the « Total à payer » box.
    const recapLines = computed<ReviewAndPayLine[]>(() => {
      if (!props.invoice) return [];
      return [
        {
          label: t('billing.repayInvoice.amountHtLine'),
          value: props.invoice.amountHt.label,
        },
      ];
    });

    const reset = () => {
      paymentError.value = null;
      paymentMode.value = hasDefaultCard.value ? 'existing' : 'new';
      paymentMethodId.value = null;
    };

    const onClose = () => {
      if (submitting.value) return;
      emit('update:visible', false);
      setTimeout(reset, 220);
    };

    const onVisibleChange = (v: boolean) => { if (!v) onClose(); };

    const onPayWithSavedCard = async () => {
      if (!props.invoice) return;
      paymentError.value = null;
      submitting.value = true;
      try {
        const result = await repayInvoice(props.invoice.id);
        await handleReload(result, {
          onFailure: (err) => {
            paymentError.value = err;
          },
          onActionRequired: () => {
            paymentError.value = t('billing.payment.actionRequired');
          },
        });
        if (result.status === 'completed') {
          emit('paid', props.invoice.id);
          emit('update:visible', false);
        }
      } finally {
        submitting.value = false;
      }
    };

    // ─── Drop-in handlers (new-card path) ────────────────────────────────
    const onDropinSubmit = (
      adyenStateData: Record<string, any>,
    ): Promise<ActionResult> => {
      if (!props.invoice) {
        return Promise.resolve({
          status: 'failed',
          error: t('billing.repayInvoice.missingInvoice'),
        });
      }
      return repayInvoice(props.invoice.id, adyenStateData);
    };

    const onDropinDetails = (
      adyenStateData: Record<string, any>,
    ): Promise<ActionResult> => confirmPlan({ adyenStateData });

    const onDropinSuccess = async () => {
      await store.dispatch('billing/refetchOverview');
      if (props.invoice) emit('paid', props.invoice.id);
      emit('update:visible', false);
    };

    const onDropinError = (err: string) => {
      paymentError.value = err;
    };

    watch(() => props.visible, (v) => { if (v) reset(); });
    watch(hasDefaultCard, (has) => {
      paymentMode.value = has ? 'existing' : 'new';
    });

    return {
      t,
      ALLOWED_METHODS_ONESHOT,
      submitting,
      paymentError,
      paymentMode,
      paymentMethodId,
      defaultCard,
      allCards,
      hasDefaultCard,
      formattedDate,
      recapLines,
      onVisibleChange,
      onClose,
      onPayWithSavedCard,
      onDropinSubmit,
      onDropinDetails,
      onDropinSuccess,
      onDropinError,
    };
  },
});
